import { apiService } from '../services/apiService';
import { paginationService } from '../services/paginationService';
import { getUsersByWorkplace } from 'core/API/checklist';

const getWorkplaceId = (): number => {
	const id = localStorage.getItem('workplace-id');
	return id ? Number(id) : 1;
};

const getCurrentUserId = (): number => {
	const id = localStorage.getItem('userId');
	return id ? Number(id) : 1;
};

export interface IToDoPost {
	description: string;
	log: any | null;
	user: string | null;
	dueDate: string;
	note: string;
	organization: string;
	geolocation: string[];
	workplace: string;
	priority: string; // Now an API endpoint like "/api/priorities/3"
	status: string; // Now an API endpoint like "/api/statuses/1"
	assignedGroup?: string | null;
	author?: string;
}

export interface ITodoCategory {
	'@context'?: string;
	'@id'?: string;
	'@type'?: string;
	id: number;
	label: string;
}


export interface GetToDosParams {
	workplaceId?: number;
	orderBy?: string;
	direction?: 'ASC' | 'DESC';
	limit?: number;
	page?: number;
	itemsPerPage?: number;
}
 
export const PostToDo = async (body: Partial<IToDoPost>): Promise<any> => {
	const workplaceId = getWorkplaceId();
	const userId = getCurrentUserId();
	const payload: any = {
		...body,
		workplace: body?.workplace || `/api/workplaces/${workplaceId}`,
		author: body?.author || `/api/users/${userId}`,
	};
	const { ...data } = apiService.responseHandler(await apiService.post(`to_dos`, payload));

	return data;
};

// Get Categorized Todos API
export const getCategorizedToDos = async ({
	page = 1,
	itemsPerPage = 10,
	workplaceId,
}: GetToDosParams): Promise<any> => {
	const wp = workplaceId ?? getWorkplaceId();
	const query = `todos/categorized?page=${page}&itemsPerPage=${itemsPerPage}&pagination=false&workplace=${wp}`;
	const { ...data } = apiService.responseHandler(await apiService.get(query));
	return data;
};

export const getToDoPrioritys = async (): Promise<any> => {
	const wp = getWorkplaceId();
	const query = `priorities?page=1&itemsPerPage=50&workplace=${wp}`;
	const { ...data } = apiService.responseHandler(await apiService.get(query));
	return data;
};


export const getToDoStatus = async (): Promise<any> => {
	const wp = getWorkplaceId();
	const query = `statuses?page=1&itemsPerPage=50&workplace=${wp}`;
	const { ...data } = apiService.responseHandler(await apiService.get(query));
	return data;
};


export const getToDoUser = async (): Promise<any> => {
	const wp = getWorkplaceId();
	// Use centralized workplace-scoped users service and return normalized array
	const users = await getUsersByWorkplace(wp);
	return users;
};


export const getTodoById = async (id: number): Promise<any> => {
	const response = await apiService.get(`to_dos/${id}`);
	const data = await apiService.responseHandler(response);
	return data;
};

export const UpdateToDo = async (id: number, body: any): Promise<any> => {
  const response = await apiService.put(`to_dos/${id}`, body);
  return apiService.responseHandler(response);
};

export const DeleteToDo = async (id: number): Promise<any> => {
  const response = await apiService.delete(`to_dos/${id}`);
  return apiService.responseHandler(response);
};

// Get Checklist Groups for Todo Assignment
export const getChecklistGroupsForTodo = async (): Promise<any> => {
  const wp = getWorkplaceId();
  return paginationService.getWithHandler(`checklist_groups?workplace=${wp}`, undefined, 30);
};

const normalizeTodoCategories = (data: any): ITodoCategory[] => {
	if (Array.isArray(data)) {
		return data;
	}

	if (data && typeof data === 'object') {
		if (Array.isArray(data.member)) {
			return data.member;
		}

		if (Array.isArray(data['hydra:member'])) {
			return data['hydra:member'];
		}

		if (Array.isArray(data.data)) {
			return data.data;
		}
	}

	return [];
};

export const getAllTodoCategories = async (): Promise<ITodoCategory[]> => {
	const wp = getWorkplaceId();
	const { ...data } = apiService.responseHandler(
		await apiService.get(`todo_categories?page=1&workplace=${wp}`)
	);
	return normalizeTodoCategories(data);
};

export const getTodoCategoryById = async (id: number): Promise<ITodoCategory> => {
	const { ...data } = apiService.responseHandler(await apiService.get(`todo_categories/${id}`));
	return data;
};

export const createTodoCategory = async (payload: { label: string }): Promise<ITodoCategory> => {
	const wp = getWorkplaceId();
	const userId = getCurrentUserId();
	const body = {
		label: payload.label,
		workplace: `/api/workplaces/${wp}`,
		author: `/api/users/${userId}`,
	};
	const { ...data } = apiService.responseHandler(await apiService.post('todo_categories', body));
	return data;
};

export const updateTodoCategory = async (
	id: number,
	payload: { label: string }
): Promise<ITodoCategory> => {
	const { ...data } = apiService.responseHandler(
		await apiService.put(`todo_categories/${id}`, { label: payload.label })
	);
	return data;
};

export const deleteTodoCategory = async (id: number): Promise<any> => {
	const { ...data } = apiService.responseHandler(await apiService.delete(`todo_categories/${id}`));
	return data;
};
